import { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { it, enUS } from 'date-fns/locale';
import { MessageCircle, Star } from 'lucide-react';
import { Button } from '../ui/button';
import { useToast } from '../ui/use-toast';
import { Avatar, AvatarFallback, AvatarImage } from '../ui/avatar';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '../ui/table';
import { sendsService } from '../../services/sends';
import CommentsDialog from '../comments/CommentsDialog';
import { formatSendDate } from './send-date';
import { SendAttemptsLabel } from './SendAttemptsLabel';
import type { Send } from '../../types/send';

interface ClimbSendsListProps {
  climbSlug: string;
  className?: string;
  refreshKey?: number;
  onAddSend?: () => void;
  initialAscentLogId?: string;
  initialCommentId?: string;
}

const INITIAL_DISPLAY_COUNT = 10;

export default function ClimbSendsList({ climbSlug, className = '', refreshKey, onAddSend, initialAscentLogId, initialCommentId }: ClimbSendsListProps) {
  const { t, i18n } = useTranslation();
  const { toast } = useToast();
  const [sends, setSends] = useState<Send[]>([]);
  const [loading, setLoading] = useState(false);
  const [showAll, setShowAll] = useState(false);
  const [selectedAscentLogId, setSelectedAscentLogId] = useState<string | null>(initialAscentLogId ?? null);
  const [scrollToCommentId, setScrollToCommentId] = useState<string | undefined>(initialCommentId);
  const dateLocale = i18n.language === 'it' ? it : enUS;

  useEffect(() => {
    const loadSends = async () => {
      try {
        setLoading(true);
        const response = await sendsService.getClimbSends(climbSlug);

        // Sort by sent_at descending (most recent first)
        const sortedSends = response.data.sort((a, b) =>
          new Date(b.attributes.sent_at).getTime() - new Date(a.attributes.sent_at).getTime()
        );

        setSends(sortedSends);
      } catch (error) {
        console.error('Error loading sends:', error);
        toast({
          title: t('sends.loadError'),
          variant: 'destructive',
        });
      } finally {
        setLoading(false);
      }
    };

    loadSends();
  }, [climbSlug, refreshKey]); // eslint-disable-line react-hooks/exhaustive-deps

  if (loading) {
    return (
      <div className={className}>
        <div className="text-center text-muted-foreground py-4">
          {t('common.loading')}
        </div>
      </div>
    );
  }

  if (sends.length === 0) {
    return (
      <div className={className}>
        <h3 className="text-lg font-semibold mb-4">{t('sends.title')}</h3>
        <div className="text-center text-muted-foreground py-4">
          {onAddSend ? (
            <button
              onClick={onAddSend}
              className="text-primary underline hover:opacity-80 transition-opacity"
            >
              {t('sends.noSends')}
            </button>
          ) : (
            t('sends.noSends')
          )}
        </div>
      </div>
    );
  }

  const displayedSends = showAll ? sends : sends.slice(0, INITIAL_DISPLAY_COUNT);
  const hasMore = sends.length > INITIAL_DISPLAY_COUNT;

  return (
    <div className={className}>
      <h3 className="text-lg font-semibold mb-4">
        {t('sends.title')} ({sends.length})
      </h3>

      <div className="rounded-md border">
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead className="w-[60px]"></TableHead>
              <TableHead>{t('common.user')}</TableHead>
              <TableHead>{t('common.date')}</TableHead>
              <TableHead className="text-right">{t('sends.attempts')}</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {displayedSends.map((send) => {
              const { user } = send.attributes;
              const hasComments = (send.attributes.comments_count ?? 0) > 0;
              const canOpenComments = !!send.attributes.ascent_log_id;

              return (
                <TableRow
                  key={send.id}
                  className={canOpenComments ? 'hover:bg-muted/50 cursor-pointer' : 'hover:bg-muted/50'}
                  onClick={canOpenComments ? () => { setSelectedAscentLogId(send.attributes.ascent_log_id!); setScrollToCommentId(undefined); } : undefined}
                >
                  <TableCell>
                    <Avatar className="h-10 w-10">
                      <AvatarImage src={user.avatar_small_url} alt={user.username} />
                      <AvatarFallback>
                        {user.username?.charAt(0)?.toUpperCase() || 'U'}
                      </AvatarFallback>
                    </Avatar>
                  </TableCell>
                  <TableCell>
                    <div>
                      <a
                        href={`/users/${user.slug}`}
                        className="font-medium hover:text-primary transition-colors"
                        onClick={(e) => e.stopPropagation()}
                      >
                        {user.username}
                      </a>
                      {send.attributes.latest_comment_preview && (
                        <p className="text-xs text-muted-foreground truncate max-w-[160px] mt-0.5">
                          {/* A tag reads as the handle it was written as, not as the slug behind it. */}
                          {send.attributes.latest_comment_preview.replace(/@\[([^\]]+)\]\([^)]+\)/g, '@$1')}
                        </p>
                      )}
                    </div>
                  </TableCell>
                  <TableCell className="text-muted-foreground">
                    {formatSendDate(send.attributes.sent_at, t, dateLocale)}
                  </TableCell>
                  <TableCell className="text-right">
                    <div className="flex flex-col items-end gap-1">
                      {send.attributes.rating != null && send.attributes.rating > 0 && (
                        <div className="flex items-center gap-0.5">
                          {[1, 2, 3, 4, 5].map((i) => {
                            const rating = send.attributes.rating!;
                            const filled = rating >= i;
                            const half = !filled && rating >= i - 0.5;
                            return (
                              <span key={i} className="relative inline-block">
                                {half ? (
                                  <>
                                    <Star className="h-3 w-3 text-muted-foreground/30" strokeWidth={1.5} />
                                    <span className="absolute inset-0 overflow-hidden" style={{ width: '50%' }}>
                                      <Star className="h-3 w-3 fill-yellow-400 text-yellow-400" strokeWidth={1.5} />
                                    </span>
                                  </>
                                ) : (
                                  <Star className={`h-3 w-3 ${filled ? 'fill-yellow-400 text-yellow-400' : 'text-muted-foreground/30'}`} strokeWidth={1.5} />
                                )}
                              </span>
                            );
                          })}
                        </div>
                      )}
                    <div className="flex items-center justify-end gap-2">
                      {hasComments && (
                        <span className="inline-flex items-center gap-1 text-xs text-muted-foreground">
                          <MessageCircle className="h-3.5 w-3.5" />
                          {send.attributes.comments_count}
                        </span>
                      )}
                      <SendAttemptsLabel
                        sendType={send.attributes.send_type}
                        attemptsCount={send.attributes.attempts_count}
                      />
                    </div>
                    </div>
                  </TableCell>
                </TableRow>
              );
            })}
          </TableBody>
        </Table>
      </div>

      {hasMore && !showAll && (
        <div className="mt-4 text-center">
          <Button
            variant="outline"
            onClick={() => setShowAll(true)}
          >
            {t('sends.loadMore', { count: sends.length - INITIAL_DISPLAY_COUNT })}
          </Button>
        </div>
      )}

      {showAll && hasMore && (
        <div className="mt-4 text-center">
          <Button
            variant="ghost"
            size="sm"
            onClick={() => {
              setShowAll(false);
              // Scroll back to the top of the sends list
              window.scrollTo({ top: 0, behavior: 'smooth' });
            }}
          >
            {t('sends.showLess')}
          </Button>
        </div>
      )}

      {selectedAscentLogId && (
        <CommentsDialog
          open={!!selectedAscentLogId}
          onOpenChange={(open) => { if (!open) { setSelectedAscentLogId(null); setScrollToCommentId(undefined); } }}
          ascentLogId={selectedAscentLogId}
          scrollToCommentId={scrollToCommentId}
        />
      )}
    </div>
  );
}
